// src/components/BrainTumor.js
import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { doc, getDoc, updateDoc, collection, addDoc, increment } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { deductCredits } from '../utils/credits';
import ReportTemplate from '../utils/ReportTemplate';
import Chart from 'chart.js/auto';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const BrainTumor = () => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [result, setResult] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [probabilities, setProbabilities] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiUrl, setApiUrl] = useState('https://brain-tumor-api-155252682785.europe-west1.run.app/predict');
  const [labelName, setLabelName] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [isFileHovering, setIsFileHovering] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const navigate = useNavigate();
  const auth = getAuth();
  const imageRef = useRef(null);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  
  const tumorTypeDescriptions = {
    'glioma': 'A type of tumor that occurs in the brain and spinal cord. Gliomas begin in the glial cells that surround and support nerve cells.',
    'meningioma': 'A tumor that forms on membranes that cover the brain and spinal cord inside the skull. Meningiomas are usually noncancerous.',
    'no tumor': 'No tumor detected in the MRI scan. The brain appears normal without abnormal growth or mass.',
    'pituitary': 'A tumor that forms in the pituitary gland located at the base of the brain. Most pituitary tumors are noncancerous.'
  };

  useEffect(() => {
    const fetchApiSettings = async () => {
      try {
        const apiSettingsRef = doc(db, 'settings', 'api');
        const apiSettingsSnap = await getDoc(apiSettingsRef);
        if (apiSettingsSnap.exists()) {
          const data = apiSettingsSnap.data();
          setApiUrl(data.brainTumorApi?.url || 'https://brain-tumor-api-155252682785.europe-west1.run.app/predict');
        }
      } catch (err) {
        console.error('Error loading API settings:', err);
      }
    };
    fetchApiSettings();

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate, auth]);

  useEffect(() => {
    // Create chart when probabilities change and results are shown
    if (showResults && Object.keys(probabilities).length > 0 && chartRef.current) {
      // Destroy existing chart if it exists
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      
      // Prepare data for chart
      const data = {
        labels: Object.keys(probabilities).map(key => key.charAt(0).toUpperCase() + key.slice(1)),
        datasets: [{
          label: 'Probability (%)',
          data: Object.values(probabilities).map(value => (value * 100).toFixed(2)),
          backgroundColor: [
            'rgba(255, 99, 132, 0.7)',
            'rgba(54, 162, 235, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(153, 102, 255, 0.7)'
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)'
          ],
          borderWidth: 1
        }]
      };

      // Create new chart
      chartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: data,
        options: {
          indexAxis: 'y',
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `${context.formattedValue}%`;
                }
              }
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              max: 100,
              title: {
                display: true,
                text: 'Probability (%)'
              }
            }
          },
          responsive: true,
          maintainAspectRatio: false
        }
      });
    }
  }, [probabilities, showResults]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    processFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsFileHovering(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const processFile = (selectedFile) => {
    setFile(selectedFile);
    setResult('');
    setConfidence(0);
    setProbabilities({});
    setError('');
    setShowResults(false);

    if (selectedFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewUrl(event.target.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handlePrediction = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!file) {
      setError('Please select an MRI image file.');
      return;
    }
    if (!labelName.trim()) {
      setError('Please enter a label name.');
      return;
    }

    setLoading(true);
    setError('');
    setUploadProgress(0);
    
    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 300);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      // Deduct credits
      const creditsToDeduct = 15;
      await deductCredits(user.uid, creditsToDeduct);

      // Upload image to Firebase Storage
      const storageRef = ref(storage, `medical_images/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const imageUrl = await getDownloadURL(storageRef);
      const bucketPath = `gs://${storage.app.options.storageBucket}/${storageRef.fullPath}`;
      
      console.log('Image uploaded successfully:', { imageUrl, bucketPath });

      // Send the image to the brain tumor prediction API
      console.log('Sending to prediction API:', apiUrl);
      const predictionFormData = new FormData();
      predictionFormData.append('file', file);
      predictionFormData.append('bucket_path', bucketPath);
      
      const predictionResponse = await fetch(apiUrl, {
        method: 'POST',
        body: predictionFormData,
      });

      if (!predictionResponse.ok) {
        const responseText = await predictionResponse.text();
        console.error('Prediction failed with status:', predictionResponse.status);
        console.error('Response text:', responseText);
        
        let errorMessage = 'Failed to get prediction';
        try {
          if (responseText.trim().startsWith('{')) {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.error || errorMessage;
          } else {
            errorMessage = `Prediction failed (${predictionResponse.status}): ${responseText.slice(0, 100)}`;
          }
        } catch (e) {
          console.error('Error parsing API error response:', e);
          errorMessage = `Prediction failed (${predictionResponse.status})`;
        }
        throw new Error(errorMessage);
      }

      const data = await predictionResponse.json();
      
      // Finish progress animation
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Set results from API response
      setResult(data.prediction);
      setConfidence(data.confidence * 100);
      setProbabilities(data.probabilities);
      setShowResults(true);

      // Save prediction to history
      await addDoc(collection(db, 'prediction_history'), {
        userId: user.uid,
        result: data.prediction,
        confidence: data.confidence * 100,
        probabilities: data.probabilities,
        date: new Date().toISOString(),
        imageUrl,
        bucketPath,
        type: 'Brain MRI',
        name: user.email || 'Anonymous',
        labelName: labelName.trim(),
      });

      // Clear the labelName input after successful submission
      setLabelName('');
    } catch (err) {
      console.error('Prediction error:', err);
      setError(err.message);
      
      // Refund credits if the prediction failed
      try {
        const user = auth.currentUser;
        if (user) {
          await updateDoc(doc(db, 'users', user.uid), {
            credits: increment(15) // Refund the credits
          });
        }
      } catch (refundError) {
        console.error('Error refunding credits:', refundError);
      }
    } finally {
      clearInterval(progressInterval);
      setUploadProgress(100);
      setLoading(false);
    }
  };

  const downloadReportAsPDF = () => {
    // Get detailed clinical description based on the tumor type
    let clinicalDescription = "The MRI scan has been analyzed by artificial intelligence.";
    if (result && tumorTypeDescriptions[result]) {
      clinicalDescription += " " + tumorTypeDescriptions[result];
    }
    
    if (result === 'no tumor') {
      clinicalDescription += " The brain appears normal without abnormal growth or mass.";
    } else if (result) {
      clinicalDescription += " Further clinical correlation and follow-up is recommended.";
    }
    
    // Get appropriate recommendations based on tumor type
    let recommendations = [];
    
    if (result === 'no tumor') {
      recommendations = [
        "No specific action required based on this MRI",
        "If patient has persistent symptoms, clinical reassessment is recommended",
        "Follow standard clinical protocols based on patient's presenting symptoms"
      ];
    } else if (result === 'glioma') {
      recommendations = [
        "Neurosurgical consultation is recommended",
        "Consider additional imaging studies (such as contrast-enhanced MRI)",
        "Biopsy may be required for definitive diagnosis and grading",
        "Clinical correlation with neurological symptoms"
      ];
    } else if (result === 'meningioma') {
      recommendations = [
        "Neurosurgical consultation is recommended",
        "Consider contrast-enhanced MRI for better characterization",
        "Follow-up imaging to monitor growth if surgical intervention is deferred",
        "Clinical correlation with presenting symptoms"
      ];
    } else if (result === 'pituitary') {
      recommendations = [
        "Endocrinological evaluation is recommended",
        "Consider contrast-enhanced MRI with special attention to the sellar region",
        "Hormonal studies to assess pituitary function",
        "Neurosurgical consultation as appropriate"
      ];
    } else {
      recommendations = [
        "Clinical correlation with patient symptoms",
        "Consider additional imaging studies as appropriate",
        "Specialist referral based on imaging findings",
        "Consider follow-up imaging to monitor any changes"
      ];
    }
    
    // Generate the standardized report using the template
    ReportTemplate.generateReport({
      reportType: 'mri',
      patientId: labelName || 'Unknown',
      finding: result,
      confidence: confidence,
      probabilities: probabilities,
      description: clinicalDescription,
      recommendations: recommendations,
      imageRef: imageRef.current,
      imageUrl: previewUrl
    });
  };

  const getTumorSeverityColor = (tumorType) => {
    switch(tumorType) {
      case 'glioma':
        return 'rgb(255, 99, 132)';
      case 'meningioma':
        return 'rgb(255, 159, 64)';
      case 'pituitary':
        return 'rgb(255, 205, 86)';
      case 'no tumor':
        return 'rgb(75, 192, 192)';
      default:
        return 'rgb(201, 203, 207)';
    }
  };

  return (
    <div className="brain-tumor min-h-screen font-['Noto Sans'] pt-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800">Brain Tumor MRI Analysis</h1>
            <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
              Cost: 15 Credits
            </div>
          </div>
          <p className="text-gray-600 mt-2">
            Upload an MRI scan image to detect and classify brain tumors using advanced AI technology.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload Section */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Upload MRI Scan</h2>
            <form onSubmit={handlePrediction} className="space-y-4">
              <div>
                <label htmlFor="labelName" className="block text-sm font-medium text-gray-700 mb-1">
                  Label/Patient ID
                </label>
                <input
                  id="labelName"
                  type="text"
                  placeholder="Enter label or patient ID"
                  value={labelName}
                  onChange={(e) => setLabelName(e.target.value)}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              
              <div 
                className={`mt-4 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isFileHovering ? 'border-purple-400 bg-purple-50' : 'border-gray-300 hover:border-purple-300'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsFileHovering(true);
                }}
                onDragLeave={() => setIsFileHovering(false)}
                onDrop={handleDrop}
              >
                {previewUrl ? (
                  <div className="space-y-4">
                    <img
                      src={previewUrl}
                      alt="MRI Preview"
                      className="max-h-48 mx-auto rounded"
                      ref={imageRef}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        setPreviewUrl(null);
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove image
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="text-sm text-gray-600">
                      Drag and drop an MRI image, or
                      <label className="relative cursor-pointer rounded-md font-medium text-purple-600 hover:text-purple-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500 ml-1">
                        <span>browse</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="sr-only"
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </div>
                )}
              </div>
              
              <button
                type="submit"
                disabled={loading || !file}
                className={`w-full px-4 py-2 rounded-md font-medium text-white transition-colors ${
                  loading || !file
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'
                }`}
              >
                {loading ? 'Processing...' : 'Analyze MRI Scan'}
              </button>
              
              {loading && (
                <div className="mt-4">
                  <div className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold inline-block text-purple-600">
                          {uploadProgress < 100 ? 'Uploading & Processing' : 'Analysis Complete'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block text-purple-600">
                          {uploadProgress}%
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-purple-200">
                      <div 
                        style={{ width: `${uploadProgress}%` }}
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500 transition-all duration-500"
                      ></div>
                    </div>
                  </div>
                </div>
              )}
              
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                  {error}
                </div>
              )}
            </form>
          </div>

          {/* Results Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-6 h-full">
              {!showResults ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className="text-lg font-medium">No Analysis Results Yet</p>
                  <p className="mt-2 text-sm text-center">Upload an MRI scan and click "Analyze MRI Scan" to see the results here.</p>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-700">Analysis Results</h2>
                    <button
                      onClick={downloadReportAsPDF}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Report
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="mb-6">
                        <div className="flex items-center">
                          <div 
                            className={`w-4 h-4 rounded-full mr-2`}
                            style={{ backgroundColor: getTumorSeverityColor(result) }}
                          ></div>
                          <h3 className="text-lg font-medium text-gray-900">Prediction</h3>
                        </div>
                        <p className="mt-1 text-2xl font-bold" style={{ color: getTumorSeverityColor(result) }}>
                          {result.charAt(0).toUpperCase() + result.slice(1)}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          Confidence Score: {confidence.toFixed(2)}%
                        </p>
                      </div>

                      {/* Tumor Description */}
                      {result && tumorTypeDescriptions[result] && (
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <h3 className="font-medium text-gray-900">Description</h3>
                          <p className="mt-2 text-sm text-gray-600">{tumorTypeDescriptions[result]}</p>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Probability Distribution</h3>
                      <div className="h-64 relative">
                        <canvas ref={chartRef}></canvas>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-500">IMPORTANT DISCLAIMER</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      The results provided by this AI system are intended for informational purposes only and should not be considered as a medical diagnosis. 
                      Even with high accuracy levels, all results should be reviewed by a qualified medical professional. 
                      Always consult with a healthcare provider for proper diagnosis and treatment decisions.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrainTumor;